import { Component, inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormField, MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInput, MatInputModule } from '@angular/material/input';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatButton } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { SignaturePadComponent } from '../../signature-pad/signature-pad.component';
import { SavingFilesComponent } from '../../saving-files/saving-files.component';
import { LoaderComponent } from '../../loader/loader.component';
import { MatCard, MatCardContent, MatCardHeader, MatCardTitle } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MessageService } from '../../../services/message.service';
import { OrdersService } from '../../../services/orders.service';
import { MatDialog } from '@angular/material/dialog';
import { Order } from '../../../classes/order.class';
import { IOrder, IOrderChangeStatus, IOrderForm, IOrderPriceUpdate } from '../../../typing/order.interface';
import { OrderPriceFormComponent } from '../order-price-form/order-price-form.component';
import { OrderStatusEnum } from '../../../enums/status.enum';
import { EnumService } from '../../../services/enum.service';
import { FileService } from '../../../services/file.service';
import { RejectFormComponent } from './reject-form/reject-form.component';
import {
    concatMap,
    debounceTime,
    filter,
    from,
    map,
    Subject,
    switchMap,
    takeUntil, tap,
    toArray,
} from 'rxjs';
import { OrderCustomFieldsComponent } from '../../order-custom-fields/order-custom-fields.component';
import { ActivatedRoute, Router } from '@angular/router';
import { MatProgressBar } from '@angular/material/progress-bar';
import { FileTypeEnum } from '../../../enums/file-type.enum';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { FocusOnFirstErrorFieldDirective } from '../../../directives/focus-on-first-error-field.directive';
import { PriceService } from '../../../services/price.service';
import { IPrice } from '../../../typing/price.interface';
import { OfflineService } from '../../offline/offline.service';
import { TimeZoneService } from '../../../services/time-zone.service';
import { amountPaidValidator } from '../../../validators/amount-paid-validator';
import { IFile } from '../../../typing/file.interface';
import { ISaveFiles } from '../../offline/offline.interface';
import { OfflineFilesService } from '../../offline/offliene.files.service';
import { AuthService } from '../../auth/auth.service';
import { FormCheckoutPaymentComponent } from './form-checkout-payment/form-checkout-payment.component';

@Component({
    selector: 'app-order-executor-form',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatFormField,
        MatInput,
        MatInputModule,
        MatCheckbox,
        MatButton,
        MatLabel,
        MatDatepickerModule,
        MatNativeDateModule,
        FormsModule,
        SignaturePadComponent,
        SavingFilesComponent,
        LoaderComponent,
        MatCard,
        MatCardContent,
        MatCardHeader,
        MatCardTitle,
        MatIcon,
        OrderPriceFormComponent,
        OrderCustomFieldsComponent,
        MatProgressBar,
        FocusOnFirstErrorFieldDirective,
    ],
    templateUrl: './order-executor-form.component.html',
    styleUrl: './order-executor-form.component.scss',
})
export class OrderExecutorFormComponent implements OnInit, OnDestroy {
    private timeZoneService = inject(TimeZoneService);
    order = new Order({} as IOrder, this.timeZoneService);
    dateRange = '';
    totalCostValue = 0;
    loading = signal<boolean>(false);
    loadingSaveForm = signal<boolean>(false);
    protected readonly OrderStatusEnum = OrderStatusEnum;
    protected readonly route = inject(ActivatedRoute);
    protected router = inject(Router);
    private fb = inject(FormBuilder);
    protected readonly FileTypeEnum = FileTypeEnum;
    protected customFieldInvalid = true;
    protected allPrices: IPrice[] = [];
    fileOffLine = signal<Partial<IFile>[]>([]);
    orderForm= this.fb.group({
        additionalInfo: [''],
        conditionsCheck: [false, Validators.requiredTrue],
        description: [''],
        signature: ['', [Validators.required]],
        files: undefined,
        orderPrices: this.fb.array([this.createOrderPriceFormGroup()]),
        prepayment: [0, [Validators.required]],
        amountPaid: [0, [Validators.required]],
        status: [OrderStatusEnum.active, [Validators.required]],
        rejectReason: [''],
        discount: false,
        agreement: '',
        rejectDescription: [''],
        customFieldsParams: undefined,
    });
    @ViewChild(OrderCustomFieldsComponent) customFieldsComponent!: OrderCustomFieldsComponent;
    orderId = this.route.snapshot.params['id'];
    fileUrls: SafeUrl[] = [];
    isEditOrderPrice: Map<number, boolean> = new Map(); //статус работ. просмотр/редактирование
    unsubscribe$ = new Subject<void>();

    constructor(
        private messageService: MessageService,
        private ordersService: OrdersService,
        private dialog: MatDialog,
        protected enumService: EnumService,
        private fileService: FileService,
        private sanitizer: DomSanitizer,
        private priceService: PriceService,
        public offlineService: OfflineService,
        private authService: AuthService,
        private offlineFilesService: OfflineFilesService
    ) {}

    ngOnInit(): void {
        if (!this.orderId) {
            this.addSubscriptions();
            return;}

        this.getOrder();

        if (this.offlineService.offline()) {
            /** для отображения фото сохранные в indexDB кода offline */
            from(this.offlineFilesService.getOfflineFiles()).pipe(
                map(files => files.find(file => file.orderId === Number(this.orderId))),
                filter(data => !!data),
                concatMap((data: ISaveFiles) =>  {
                    return from(Array.from(data.files)).pipe(
                        concatMap((res: File) => this.offlineFilesService.fileToBase64(res)
                            /** Преобразуем File в base64 */
                            .pipe(
                                map((file) => {
                                    return {
                                        ...file,
                                        buffer:  this.sanitizer.bypassSecurityTrustUrl(
                                            URL.createObjectURL(this.createBlobFromBase64(file.buffer || ''))) as string
                                    };
                                })
                            )
                        )
                    );
                }),
                toArray(),
            ).subscribe((newFiles) => {
                if (newFiles.length) {
                    this.fileOffLine.update((prev) => [...prev, ...newFiles]);
                }
            });
        }

    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    getOrder():void {
        const offline = this.offlineService.offline();

        const order$ = offline ?
            this.offlineService.getOrderExecutorCache(Number(this.orderId)).pipe(
                takeUntil(this.unsubscribe$),
            ): this.ordersService.getOrder(Number(this.orderId)).pipe(
                tap(() =>  this.offlineService.loadDateOrderOffline$.next())
            );

        if(!offline) {
            this.loading.set(true);
        }

        order$.pipe(filter(order => !!order)).subscribe({
            next: order => {
                this.order.data = order;
                this.dateRange = `${order.dateFrom},${order.dateTo}`;

                if (order.files) {
                    this.fileUrls = order.files.map(file =>
                        this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(this.createBlobFromBase64(file.buffer))));
                }
                // получаем массив всех контролов
                const pricesArray = this.orderForm.controls.orderPrices;
                if (pricesArray.length === 1 && pricesArray.at(0).value.priceId === 0) {
                    pricesArray.removeAt(0);
                }

                // наполняем массив данными
                order.orderPrices?.forEach(data => {
                    if (data && data.price?.id) {
                        pricesArray.push(
                            this.fb.group({
                                id: [data.id || null],
                                priceId: [data.price.id, Validators.required],
                                itemId: [data.item?.id || null],
                                quantity: [data.quantity || 1, [Validators.min(1), Validators.required]],
                                priceValue: [data.customPrice ?? data.price.price],
                                discountPriceValue: [data.customPrice ?? data.price.discountPrice],
                                customPrice: [data.customPrice ?? null],
                            })
                        );
                    }
                });

                this.orderForm.patchValue(this.order.formData(), {emitEvent: false});
                this.checkStatusForm();
                this.addSubscriptions();

                // инициализируем общую стоимость при первом вызове компонента
                this.totalCostValue = this.order.totalCost(
                    this.orderForm.controls.orderPrices.value,
                    !!this.orderForm.controls.discount.value,
                );
                this.amountPaidUpdate(this.totalCostValue);

                // записываем все работы в объект, и устанавливаем режим просмотра
                this.orderForm.controls.orderPrices.controls.forEach((control, index) => {
                    this.isEditOrderPrice.set(index, true);
                });

                if (!offline) {
                    this.loadAllPrices();
                }
            },
            error: () => this.messageService.errorMessage('Задачи не существует'),
        }).add(() => this.loading.set(false));
    }

    addSubscriptions(): void {
        this.orderForm.controls.status.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(status => {
            if (status === OrderStatusEnum.atwork) {
                this.orderForm.controls.signature.setValidators(Validators.required);
                this.orderForm.controls.conditionsCheck.setValidators(Validators.requiredTrue);
            }
        });

        this.orderForm.controls.orderPrices.valueChanges.pipe(debounceTime(500), takeUntil(this.unsubscribe$)).subscribe((pricesValue) => {
            this.totalCostValue = this.order.totalCost(pricesValue, !!this.orderForm.controls.discount.value);
            this.amountPaidUpdate(this.totalCostValue);
        });
    }

    amountPaidUpdate(total: number): void {
        const amountPaidControl = this.orderForm.controls.amountPaid;
        const prepayment = this.order.data.prepayment ?? 0;
        const newAmountPaid = Number((total - prepayment).toFixed(2));
        amountPaidControl.setValue(newAmountPaid, { emitEvent: false });

        amountPaidControl.clearValidators();
        amountPaidControl.setValidators([
            Validators.required,
            amountPaidValidator(total, prepayment)
        ]);
        amountPaidControl.updateValueAndValidity({ emitEvent: false });
    }

    onSubmit(status?: IOrderChangeStatus): void {
        if (this.offlineService.offline()) {
            this.order.data.status = status ? status.status : OrderStatusEnum.done;
            this.offlineService.updateOfflineOrdersList$(this.order.data);
            this.messageService.successMessage('Статус заявки изменен');
        }

        if (!this.orderForm.valid && !status) {
            this.messageService.infoMessage('Заполнены не все данные');
            return;
        }

        if (!this.offlineService.offline()) {
            this.loading.set(true);
        }

        this.ordersService.updateOrder(this.order.dataForUpdate({
            status: status ? status.status : OrderStatusEnum.done,
        } as IOrderForm)).subscribe({
            next: (order) => {
                this.messageService.successMessage('Статус заявки изменен');
                this.order.data.status = order.status;
            },
            error: error => {
                if (!this.offlineService.offline()) {
                    this.messageService.errorMessage(error.message);
                }
            }
        }).add(() => this.loading.set(false));
    }

    // редактирование заявки после ее выполнения
    updateOrder(): void {
        if (this.offlineService.offline()) {

            if (this.orderForm.controls.files.value && this.order.data.status === OrderStatusEnum.done) {
                this.saveOrderFiles();
            }

            if (this.orderForm.value.status) {
                this.order.data.status = this.orderForm.value.status;
            }
            this.offlineService.updateOfflineOrdersList$(this.order.data);
            this.offlineService.updateOfflineOrderExecutor(this.order.data, this.orderForm.value as IOrderForm);
            this.messageService.successMessage('Заявка успешно сохранена');

            this.openActual();

            ///для обновления данных в сервисе
        }

        if (!this.offlineService.offline()) {
            this.loading.set(true);
        }

        this.ordersService.updateOrder(
            this.order.dataForUpdate(this.orderForm.value as IOrderForm)
        ).subscribe({
            next: (order) => {
                this.messageService.successMessage('Заявка успешно сохранена');
                this.order.data = order;
                if (this.orderForm.controls.files.value) {
                    this.saveOrderFiles();
                }

                this.orderForm.patchValue(this.order.formData());
                if (order.status === OrderStatusEnum.reject || order.status === OrderStatusEnum.done) {this.openActual();}
            },
            error: (error) => {
                if (!this.offlineService.offline()) {
                    this.messageService.errorMessage(error.message);
                }
            }
        }).add(() => this.loading.set(false));
    }

    //Сохраняем файлы
    saveOrderFiles(): void {
        const files = this.orderForm.controls.files.value as FileList;

        // Создаём новый массив файлов с обновлёнными именами
        const renamedFiles = Array.from(files).map((file, index) => {
            const fileNameParts = file.name.split('.');
            const extension = fileNameParts.pop();
            const timestamp = new Date().getTime();
            const newFileName = `${timestamp}${index}_${fileNameParts.join('.')}.${extension}`;
            return new File([file], newFileName, { type: file.type });
        });

        // Создаём новый FileList из массива файлов
        const newFileList = this.createFileList(renamedFiles);
        if (this.offlineService.offline()) {
            this.offlineFilesService.saveFilesToIndexedDB(Array.from(newFileList), this.order.data.id);
        }

        this.fileService.uploadOrdersFiles(this.order.data.id, newFileList).subscribe({
            next: () => this.orderForm.controls.files.reset(),
            error: error => {
                if (!this.offlineService.offline()) {
                    this.messageService.errorMessage(error.message);
                }
            },
        });
    }

    addOrderPrice(): void {
        const index = this.isEditOrderPrice.size;
        this.isEditOrderPrice.set(index, false); //добавленной работе сразу ставим режим редактирования
        this.orderForm.controls.orderPrices.push(this.createOrderPriceFormGroup());
    }

    // удаляем объект из массива orderPrices
    removeOrderPrice(index: number): void {
        this.messageService.confirmMessage({
            title: 'Вы действительно хотите удалить работу?',
            primaryText: 'Подтвердить',
            secondaryText: 'Отменить',
            showSecondaryButton: true,
        }).pipe(
            filter(confirm => confirm)
        ).subscribe(() => {
            if (this.offlineService.offline()) {
                const id = this.orderForm.controls.orderPrices.value.at(index).id;
                this.offlineService.removePriceOrder(this.order.data, id);
            }
            this.orderForm.controls.orderPrices.removeAt(index);
            this.deleteOrderPrice();
        });
    }

    // патчим кастомные поля в форму
    updateCustomFieldsControl(): void {
        const customFieldsValue = this.customFieldsComponent.getCustomFieldsValue();
        this.orderForm.controls.customFieldsParams.patchValue(customFieldsValue);
        this.orderForm.controls.customFieldsParams.markAsDirty(); // помечаем контрол dirty, чтобы saveControl знал, что значение контрола было изменно
        this.saveControl();
    }

    // помечаем контрол подписи dirty, чтобы saveControl знал, что значение контрола было изменно
    updateSignature(): void {
        this.orderForm.controls.signature.markAsDirty();
        this.saveControl();
    }

    // Сохранение файлов, когда заявка находится в статусе "в работе"
    updateFiles(event: Event): void {
        const { files } = event.target as HTMLInputElement;
        if (!files || files.length === 0) {
            return;
        }
        // Обновляем значение в форме
        this.orderForm.controls.files.setValue(files);

        // Сохраняем файл и обновляем данные файлов ордера
        if (this.orderForm.controls.files.value) {

            if (this.offlineService.offline()) {
                this.offlineFilesService.saveFilesToIndexedDB(Array.from(files), this.order.data.id);

                from(Array.from(files)).pipe(
                    concatMap((files) => this.offlineFilesService.fileToBase64(files).pipe(
                        map((file) => {
                            return {
                                ...file,
                                buffer:  this.sanitizer.bypassSecurityTrustUrl(
                                    URL.createObjectURL(this.createBlobFromBase64(file.buffer || ''))) as string
                            };
                        })
                    )),
                    toArray(),
                ).subscribe((newFiles) => {
                    this.fileOffLine.update((prev) => [...prev, ...newFiles]);
                });

            }

            if (!this.offlineService.offline()) {
                this.loadingSaveForm.set(true);
            }

            // Создаём новый массив файлов с обновлёнными именами
            const newFiles = Array.from(files).map((file) => {
                const fileNameParts = file.name.split('.');
                const extension = fileNameParts.pop();
                const newFileName = `${new Date().getTime()}_${fileNameParts.join('.')}.${extension}`;
                return new File([file], newFileName, { type: file.type });
            });

            // Создаём новый FileList из массива файлов
            const newFileList = this.createFileList(newFiles);

            // Отправляем новые файлы на сервер
            this.fileService.uploadOrdersFiles(this.order.data.id, newFileList).pipe(
                switchMap(() => {
                    return this.ordersService.getOrder(Number(this.orderId));
                })
            ).subscribe({
                next: order => {
                    this.order.data.files = order.files;
                    this.orderForm.controls.files.reset();
                },
                error: error => {
                    if (!this.offlineService.offline()) {
                        this.messageService.errorMessage(error.message);
                    }
                },
            }).add(() => this.loadingSaveForm.set(false));
        }
    }

    // Вспомогательная функция для создания FileList из массива файлов
    createFileList(files: File[]): FileList {
        const dataTransfer = new DataTransfer();
        files.forEach(file => dataTransfer.items.add(file));
        return dataTransfer.files;
    }

    // изменение статуса заявки
    changeStatus(status: OrderStatusEnum): void {
        this.orderForm.controls.status.setValue(status);
        this.checkStatusForm();
        if (status === OrderStatusEnum.done) {

            if (this.orderForm.invalid || this.customFieldInvalid) {
                this.messageService.infoMessage('Заполнены не все данные');
                return;
            }

            this.dialog.open(FormCheckoutPaymentComponent, {
                width: '95%',
                maxWidth: '600px',
                data: this.order,
            }).afterClosed().pipe(
                filter(confirm => !!confirm)
            ).subscribe(() => {
                this.orderForm.controls.status.setValue(OrderStatusEnum.done);
                this.updateOrder();
            });

        } else {
            this.onSubmit({ status: status } as IOrderChangeStatus);
        }
    }

    // изменение статуса заявки на "отказ"
    rejectOrder():void {
        this.dialog.open(RejectFormComponent, {
            width: '98%',
            maxWidth: '600px',
            height: '80%',
        }).afterClosed().pipe(
            filter(reject => !!reject)
        ).subscribe(reject => {
            this.orderForm.patchValue({
                status: OrderStatusEnum.reject,
                rejectReason: reject.rejectReason,
                rejectDescription: reject.rejectDescription
            });
            this.updateOrder();
        });
    }

    get canEdit(): boolean {
        const lastUpdateDate = new Date(this.order.data.updatedAt);
        const today = new Date();
        const oneDay = 24 * 60 * 60 * 1000; // количество миллисекунд в одном дне
        const diff = Math.abs(lastUpdateDate.getTime() - today.getTime()); // абсолютная разница между датами
        return diff <= oneDay; // возвращает true, если разница не больше суток
    }

    openActual(): void {
        this.router.navigate(['orders-list']);
    }

    // сохранение контрола при изменении данных
    saveControl(): void {
        if (this.order.data.status !== OrderStatusEnum.atwork || this.orderForm.controls.orderPrices.invalid) {
            return;
        }

        const changedControl = Object.keys(this.orderForm.controls)
            .find(controlName => this.orderForm.get(controlName)?.dirty);

        if(!changedControl) {
            return;
        }

        if (!this.offlineService.offline()) {
            this.loadingSaveForm.set(true);
        }

        // this.loadingSaveForm.set(true);

        this.ordersService.updateOrder(this.order.dataForUpdate({
            [changedControl]: this.orderForm.get(changedControl)?.value,
        } as IOrderForm)).subscribe({
            error: error => {
                if (!this.offlineService.offline()) {
                    this.messageService.errorMessage(error.message);
                }
            }
        }).add(() => {
            this.loadingSaveForm.set(false);
            this.orderForm.markAsPristine();
        });
    }

    // возможность изменения формы только в статусе "в работе"
    checkStatusForm():void {
        if (this.orderForm.controls.status.value !== OrderStatusEnum.atwork && this.orderForm.controls.status.value !== OrderStatusEnum.done) {
            this.orderForm.disable();
        } else {
            this.orderForm.enable();
        }
    }

    private createOrderPriceFormGroup(): FormGroup {
        return this.fb.group({
            priceId: [0, Validators.required],
            itemId: [0],
            quantity: [1, [Validators.min(1), Validators.required]],
            priceValue: [0, [Validators.min(0)]],
            discountPriceValue: [0, [Validators.min(0)]],
            customPrice: [null, [Validators.min(0)]],
        });
    }

    createBlobFromBase64(b64Data: string, contentType = ''): Blob {
        const byteCharacters = atob(b64Data);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], { type: contentType });
    }

    //меняем статус работы просмотр/редактирование
    toggleEditOrderPrice(index:number): void {
        this.isEditOrderPrice.set(index, !this.isEditOrderPrice.get(index));
    }

    // сохраняем редактирование работы
    applyEditOrderPrice(index:number):void {

        if (this.offlineService.offline()) {
            const formOrder =  this.orderForm.controls.orderPrices.value.at(index) as  IOrderPriceUpdate;
            this.offlineService.addNewPriceOrder(this.order.data, formOrder, index);
        }

        if (!this.offlineService.offline()) {
            this.loadingSaveForm.set(true);
        }

        this.ordersService.updateOrder(this.order.dataForUpdate({
            orderPrices: this.orderForm.get('orderPrices')?.value,
        } as IOrderForm)).subscribe({
            next: order => {
                this.order.data.orderPrices[index] = order.orderPrices[index];
            },
            error: error => {
                if(!this.offlineService.offline()) {
                    this.messageService.errorMessage(error.message);
                }
            }
        }).add(() => {
            this.toggleEditOrderPrice(index);
            this.loadingSaveForm.set(false);
        });
    }

    // сохраняем список работ при удалении
    deleteOrderPrice():void {

        if (!this.offlineService.offline()) {
            this.loadingSaveForm.set(true);
        }

        this.ordersService.updateOrder(this.order.dataForUpdate({
            orderPrices: this.orderForm.get('orderPrices')?.value,
        } as IOrderForm)).subscribe({
            next: order => {
                this.order.data.orderPrices = order.orderPrices;
                // обновляем статусы
                this.isEditOrderPrice.clear();
                this.orderForm.controls.orderPrices.controls.forEach((control, index) => {
                    this.isEditOrderPrice.set(index, true);
                });
            },
            error: error => {
                if (!this.offlineService.offline()) {
                    this.messageService.errorMessage(error.message);
                }
            }
        }).add(() => {
            this.loadingSaveForm.set(false);
        });
    }

    private loadAllPrices():void {
        this.priceService.getAllPrices().subscribe({
            next: (prices: IPrice[]): void => {
                this.allPrices = prices;
            },
            error: () => this.messageService.errorMessage('Ошибка загрузки работ'),
        });
    }

    priceWithoutItems(id: number): boolean {
        const foundPrice = this.allPrices.find(price => price.id === id);

        return foundPrice?.category?.customId !== 'INSTRUCTAGE' && foundPrice?.category?.customId !== 'PROVOD';
    }

    getRowTotal(control: FormGroup): number {
        const quantity = control.get('quantity')?.value || 1;
        const priceValue = control.get('priceValue')?.value ?? 0;
        const discountPriceValue = control.get('discountPriceValue')?.value;
        const unitPrice = this.orderForm.controls.discount.value
            ? (discountPriceValue ?? priceValue)
            : priceValue;
        return Number((unitPrice * quantity).toFixed(2));
    }
}
